import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import styles from './Banner.module.css';

/**
 * Banner Component
 * Shows different banner based on login status
 */
const Banner = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Banner slides data - using slider images from withoutloginSlider
  const slides = [
    {
      id: 1,
      title: 'Welcome Bonus',
      subtitle: 'Start your journey with amazing bonuses',
      image: '/assets/images/withoutloginSlider/slider/welcome-bonus.avif',
      link: '/casino',
      mobileImage: '/assets/images/withoutloginSlider/slider/welcome-mobile.avif',
    },
    {
      id: 2,
      title: 'Sports Welcome Bonus',
      subtitle: 'Bet on your favorite sports',
      image: '/assets/images/withoutloginSlider/slider/sports-welcome-bonus.avif',
      link: '/sports',
      mobileImage: '/assets/images/withoutloginSlider/slider/sports-mobile.jpeg',
    },
    {
      id: 3,
      title: 'Slot Welcome Bonus',
      subtitle: 'Spin and win big',
      image: '/assets/images/withoutloginSlider/slider/slot-welcome-bonus.avif',
      link: '/slots',
      mobileImage: '/assets/images/withoutloginSlider/slider/slot-mobile.avif',
    },
    {
      id: 4,
      title: 'Instant Win',
      subtitle: 'Win instantly with our games',
      image: '/assets/images/withoutloginSlider/slider/instant-win.avif',
      link: '/games',
      mobileImage: '/assets/images/withoutloginSlider/slider/instant-mobile.avif',
    },
  ];

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handleSlideClick = (link) => {
    if (link && !isLoggedIn) {
      // For non-logged-in users, open signup modal instead
      navigate('/login?modalType=auth-signup');
    } else if (link && isLoggedIn) {
      // For logged-in users, navigate to the link
      navigate(link);
    }
  };

  const handleButtonClick = (e, link) => {
    e.stopPropagation(); // Prevent slide click
    if (!isLoggedIn) {
      // Open signup modal for non-logged-in users
      navigate('/login?modalType=auth-signup');
    } else if (link) {
      // Navigate to link for logged-in users
      navigate(link);
    }
  };

  return (
    <div className={styles.bannerContainer}>
      <div className={styles.bannerWrapper}>
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`${styles.slide} ${index === currentSlide ? styles.active : ''}`}
            onClick={() => handleSlideClick(slide.link)}
          >
            <div className={styles.slideBackground}>
              <img
                src={isMobile && slide.mobileImage ? slide.mobileImage : slide.image}
                alt={slide.title}
                className={styles.slideImage}
                onError={(e) => {
                  e.target.src = '/assets/images/default_card3.png';
                }}
              />
              <div className={styles.slideOverlay}>
                <div className={styles.slideContent}>
                  <h2 className={styles.slideTitle}>{slide.title}</h2>
                  <p className={styles.slideSubtitle}>{slide.subtitle}</p>
                  <button 
                    className={styles.slideButton}
                    onClick={(e) => handleButtonClick(e, slide.link)}
                  >
                    {isLoggedIn ? 'Explore Now' : 'Sign Up Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Slide Indicators */}
      <div className={styles.indicators}>
        {slides.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === currentSlide ? styles.active : ''}`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        className={styles.navButton}
        style={{ left: '1rem' }}
        onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
        aria-label="Previous slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        className={styles.navButton}
        style={{ right: '1rem' }}
        onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
        aria-label="Next slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
};

export default Banner;
